<template>
  <div v-if="(displayGlobalAlerts?.length ?? 0) > 0" class="w-full relative z-[9999]">
    <!-- GLOBAL ALERT START -->
    <GlobalAlert
      v-for="(item, itemIndex) in displayGlobalAlerts"
      :key="itemIndex"
      :type="item?.fields?.type?.value"
      :content="item?.fields?.content?.value"
      :visibility="item?.fields?.visibility?.value"
      @closeAlert="(el) => updateNavPosition(null, el)"
    ></GlobalAlert>
    <!-- GLOBAL ALERT END -->
  </div>
  <FullScreenSpinner v-if="!loaded" />
  <div v-else class="w-full h-full md:h-auto lg:flex relative">
    <Placeholder v-if="showMenu" name="jss-page-menu" :rendering="rendering" />

    <div
      class="w-full lg:min-w-0 bg-swish-sm md:bg-swish-md lg:bg-swish-lg bg-right-top bg-no-repeat md:bg-fixed flex flex-col overflow-x-hidden"
    >
      <main
        class="grow flex flex-col pt-8 pb-12 md:py-18 px-4 md:px-8 xl:px-25 w-full lg:max-w-[1144px]"
        :class="{ 'mx-auto': !showMenu }"
      >
        <div class="grow">
          <div v-if="showMenu" :class="{ 'pb-12 mb-2 border-b border-racq-white': showBreadcrumbs }">
            <div v-if="showBreadcrumbs">
              <SelfServiceBreadcrumbs />
            </div>
            <Placeholder name="jss-ss-header" :rendering="rendering" />
          </div>
          <div class="flex flex-col pt-12 w-full" :class="outerClass" :style="inlineStyle">
            <Placeholder name="jss-ss-main" :rendering="rendering" />
          </div>
        </div>

        <div v-if="context?.disclaimer?.value" class="pt-15 max-w-[60rem]" data-test-id="disclaimer_component">
          <RichText :field="context?.disclaimer as Field<string>" class="self-end" />
        </div>
      </main>

      <Placeholder name="jss-page-footer" :rendering="rendering" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { getCurrentInstance, onMounted, onUnmounted, computed, watch, ref } from 'vue';
import SelfServiceBreadcrumbs from './SelfServiceBreadcrumbs.vue';
import { ComponentRendering, Field, Placeholder, RichText } from '@sitecore-jss/sitecore-jss-vue';
import GlobalAlert from '@/components/GlobalAlert/GlobalAlert.vue';
import { GlobalAlertsVisibility } from '@/enums/global-alerts.enum';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { toBool } from '@/utils/convert';
import { useAccountStore, usePageStore, useLiveChatStore } from '@/store';
import FullScreenSpinner from '../FullScreenSpinner/FullScreenSpinner.vue';

const props = defineProps<{
  rendering: ComponentRendering;
  fields: {
    fields: INameValue[];
    breadcrumbs: INameValue[];
  };
}>();

const outerClass = props.rendering?.params?.Styles;
const inlineStyle = props.rendering?.params?.inlineStyle;
const context: Record<string, Field> = {};
props.fields.fields.forEach((item) => {
  context[item.name] = { value: item.value };
});

const showBreadcrumbs = toBool(context.showBreadcrumbs?.value);

if (showBreadcrumbs) {
  const pageStore = usePageStore();
  pageStore.breadcrumbs = props.fields.breadcrumbs;
}

const app = getCurrentInstance();
const contentEditing = app?.appContext.config.globalProperties.$jss.sitecoreContext()?.pageEditing === true;
const routeData = app?.appContext.config.globalProperties.$jss.routeData();

const displayGlobalAlerts = routeData?.fields?.GlobalAlerts?.fields?.globalAlerts?.filter(
  (x: any) =>
    x.fields?.visibility?.value === GlobalAlertsVisibility.All ||
    x.fields?.visibility?.value === GlobalAlertsVisibility.SelfService
);

const showMenu = computed(() => contentEditing || toBool(context.showNavigation?.value));

const liveChatStore = useLiveChatStore();

onMounted(() => {
  // Need to wait for styles to load. nextTick doesn't cut it unfortunately.
  setTimeout(() => {
    updateNavPosition(null);
  }, 10);

  liveChatStore.setChatEnabled(true);

  window.addEventListener('scroll', updateNavPosition);
  window.addEventListener('resize', updateNavPosition);
});

onUnmounted(() => {
  window.removeEventListener('scroll', updateNavPosition);
  window.removeEventListener('resize', updateNavPosition);
});

const getAlertsHeight = (removedAlert?: HTMLDivElement) => {
  const alerts = document.querySelectorAll('[data-global-alert]');
  let alertsHeight = 0;
  for (let alert of alerts) {
    if (alert != removedAlert) {
      alertsHeight += alert.clientHeight;
    }
  }
  return alertsHeight;
};

const updateNavPosition = (e: any | null, removedAlert?: HTMLDivElement) => {
  const drawer = document.getElementById('self-service-menu-drawer') as HTMLDivElement;
  if (!drawer) return;
  if (window.innerWidth < 992) {
    const stickyNavTop = document.getElementById('sticky-header')?.getBoundingClientRect().top ?? 0;
    const alertsHeight = getAlertsHeight(removedAlert);
    const scrollTop = document.body.style.top ? parseInt(document.body.style.top || '0') * -1 : window.scrollY;
    drawer.style.top = `${stickyNavTop == 0 ? scrollTop : stickyNavTop + scrollTop}px`;
    drawer.style.maxHeight = window.scrollY < alertsHeight ? `calc(100vh - ${alertsHeight - window.scrollY}px)` : '';
  } else {
    drawer.style.top = '';
    drawer.style.maxHeight = '';
  }
};

const accountStore = useAccountStore();
const authRequired = props.fields?.fields?.find((field) => field.name == 'AuthenticationRequired')?.value == '1';
// do not show spinner if auth not required / experience editor / d365Id loaded
const loaded = ref(!authRequired || contentEditing || !!accountStore.identityDetails?.d365Id);
!contentEditing &&
  watch(
    () => accountStore.identityDetails?.d365Id,
    (d365Id) => (loaded.value = !!d365Id)
  );
</script>
